import { useEffect, useState } from 'react';
import { BUSINESS_CATEGORIES } from './data_registry';

export type Route =
  | { name: 'home' }
  | { name: 'search'; q: string; city?: string; category?: string }
  | { name: 'business'; slug: string }
  | { name: 'city'; slug: string }
  | { name: 'category'; slug: string }
  | { name: 'blogs'; category?: string; q?: string }
  | { name: 'blog'; slug: string }
  | { name: 'products'; category?: string; city?: string; q?: string }
  | { name: 'product_detail'; slug: string };

function capitalize(str: string): string {
  return str.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

export function normalizeCategory(slug: string): string {
  if (!slug) return 'Services';
  const dec = decodeURIComponent(slug).toLowerCase().replace(/-/g, ' ').replace(/\s+/g, ' ').trim();

  // 1. Check exact match in registered business categories
  const directMatch = BUSINESS_CATEGORIES.find(c => {
    const cClean = c.toLowerCase().trim();
    return cClean === dec || cClean.replace(/&/g, 'and') === dec || cClean.replace(/\band\b/g, '&') === dec;
  });
  if (directMatch) return directMatch;

  // 2. Check fuzzy match (ignoring non-alphanumeric and plurals)
  const partialMatch = BUSINESS_CATEGORIES.find(c => {
    const cClean = c.toLowerCase().replace(/[^a-z0-9]/g, '');
    const decClean = dec.replace(/[^a-z0-9]/g, '');
    return cClean === decClean || cClean === decClean + 's' || cClean + 's' === decClean;
  });
  if (partialMatch) return partialMatch;

  // 3. Fallback: Capitalize each word properly
  return dec.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function normalizeCity(slug: string): string {
  const dec = decodeURIComponent(slug).toLowerCase().replace(/-/g, ' ').trim();
  if (dec === 'delhi' || dec === 'delhi ncr' || dec === 'new delhi') return 'Delhi';
  if (dec === 'greater noida') return 'Greater Noida';
  if (dec === 'noida') return 'Noida';
  if (dec === 'gurugram' || dec === 'gurgaon') return 'Gurugram';
  if (dec === 'navi mumbai') return 'Navi Mumbai';
  return capitalize(dec);
}

const ROUTE_EVENT = 'localhub:routechange';

function emitRouteChange() {
  window.dispatchEvent(new Event(ROUTE_EVENT));
  if (typeof window !== 'undefined' && (window as any).dataLayer) {
    (window as any).dataLayer.push({
      event: 'virtualPageview',
      pagePath: window.location.pathname + window.location.search,
      pageTitle: document.title,
    });
  }
}

function currentPath(): string {
  return window.location.pathname + window.location.search;
}

function parsePath(fullPath: string): Route {
  const clean = fullPath.replace(/^\/+/, '');
  if (!clean) return { name: 'home' };
  const [path, query] = clean.split('?');
  const params = new URLSearchParams(query || '');
  const parts = path.split('/').filter(Boolean);

  // 1. Blogs and Guides
  if (parts[0] === 'blogs' || parts[0] === 'guides' || (parts[0] === 'blog' && !parts[1])) {
    return {
      name: 'blogs',
      category: params.get('category') || (parts[1] && parts[1] !== 'all' ? normalizeCategory(parts[1]) : undefined),
      q: params.get('q') || ''
    };
  }
  if (parts[0] === 'blog' && parts[1]) return { name: 'blog', slug: parts[1] };

  // 2. Business Listing Details: /b/:slug
  if (parts[0] === 'b' && parts[1]) return { name: 'business', slug: parts[1] };

  // 3. City & Category shortcuts
  if (parts[0] === 'c' && parts[1]) return { name: 'city', slug: parts[1] };
  if (parts[0] === 'cat' && parts[1]) return { name: 'category', slug: parts[1] };

  // 4. Products & E-Commerce Storefront
  if (parts[0] === 'products' || parts[0] === 'shop' || parts[0] === 'store') {
    return {
      name: 'products',
      category: params.get('category') || (parts[1] && parts[1] !== 'all' ? normalizeCategory(parts[1]) : undefined),
      city: params.get('city') || undefined,
      q: params.get('q') || ''
    };
  }
  if (parts[0] === 'product' && parts[1]) return { name: 'product_detail', slug: parts[1] };

  // 5. Natural Semantic URLs: /search/car-washes-in-bhopal OR /car-washes-in-bhopal
  let targetSegment = parts[0] === 'search' ? (parts[1] || '') : parts[0];
  if (targetSegment && targetSegment.toLowerCase().includes('-in-')) {
    const inIdx = targetSegment.toLowerCase().indexOf('-in-');
    const catPart = targetSegment.slice(0, inIdx);
    const cityPart = targetSegment.slice(inIdx + 4);
    if (catPart && cityPart) {
      return {
        name: 'search',
        q: '',
        category: normalizeCategory(catPart),
        city: normalizeCity(cityPart),
      };
    }
  }

  // 6. General Search: /search or /search/:category/:city
  if (parts[0] === 'search') {
    if (parts[1] && parts[2]) {
      return {
        name: 'search',
        q: '',
        category: normalizeCategory(parts[1]),
        city: normalizeCity(parts[2]),
      };
    }
    return {
      name: 'search',
      q: params.get('q') ?? '',
      city: params.get('city') ?? undefined,
      category: params.get('category') ?? undefined,
    };
  }

  return { name: 'home' };
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parsePath(currentPath()));
  useEffect(() => {
    const onChange = () => setRoute(parsePath(currentPath()));
    window.addEventListener('popstate', onChange);
    window.addEventListener(ROUTE_EVENT, onChange);
    return () => {
      window.removeEventListener('popstate', onChange);
      window.removeEventListener(ROUTE_EVENT, onChange);
    };
  }, []);
  return route;
}

export function navigate(path: string) {
  let clean = path.startsWith('#') ? path.slice(1) : path;
  if (!clean.startsWith('/')) clean = '/' + clean;
  if (clean === currentPath()) return;
  window.history.pushState({}, '', clean);
  emitRouteChange();
  window.scrollTo(0, 0);
}

export function replace(path: string) {
  let clean = path.startsWith('#') ? path.slice(1) : path;
  if (!clean.startsWith('/')) clean = '/' + clean;
  window.history.replaceState({}, '', clean);
  emitRouteChange();
}

export function useAdminMode(): [boolean, (v: boolean) => void] {
  const isAdminPath = () => window.location.pathname.startsWith('/admin');
  const [admin, setAdmin] = useState(isAdminPath);
  useEffect(() => {
    const onChange = () => setAdmin(isAdminPath());
    window.addEventListener('popstate', onChange);
    window.addEventListener(ROUTE_EVENT, onChange);
    return () => {
      window.removeEventListener('popstate', onChange);
      window.removeEventListener(ROUTE_EVENT, onChange);
    };
  }, []);
  const set = (v: boolean) => {
    navigate(v ? '/admin' : '/');
  };
  return [admin, set];
}

export function linkProps(path: string) {
  return {
    href: path,
    onClick: (e: React.MouseEvent) => {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.altKey ||
        e.ctrlKey ||
        e.shiftKey
      ) {
        return;
      }
      e.preventDefault();
      navigate(path);
    },
  };
}
